import { Injectable } from "@angular/core";
import { environment } from "@environments/environment";
import { IbgeCity } from "@interfaces/ibge-city";
import { IbgeState } from "@interfaces/ibge-state";
import { Option } from "@interfaces/option";
import { Store } from "@ngrx/store";
import { selectCities, selectStates } from "@state/selectors/ibge.selectors";
import { Observable, map, of, tap } from "rxjs";
import { HttpService } from "./http.service";
import { IbgeActions } from "@state/actions/ibge.actions";

@Injectable({
  providedIn: "root"
})
export class IbgeService {
  constructor(
    private httpService: HttpService,
    private store: Store
  ) {}

  private getCityCacheKey(uf: string, useIdAsValue: boolean) {
    return `${uf}:${useIdAsValue ? "ibge" : "descricao"}`;
  }

  public fetchStates(): Observable<Option[]> {
    const cached = this.store.selectSignal(selectStates);

    if (cached().length) {
      return of(cached());
    }

    return this.httpService.get<IbgeState[]>(`${environment.api.ibge}`).pipe(
      map<IbgeState[], Option[]>((states) =>
        states
          .map((state) => ({
            display: state.nome,
            value: state.sigla
          }))
          .sort((a, b) => a.display.localeCompare(b.display))
      ),
      tap((states) =>
        this.store.dispatch(
          IbgeActions.loadStates({
            states
          })
        )
      )
    );
  }

  public fetchCities(
    uf: string,
    useIdAsValue = false
  ): Observable<Option<string | number>[]> {
    const cacheKey = this.getCityCacheKey(uf, useIdAsValue);
    const cached = this.store.selectSignal(selectCities(cacheKey));

    if (cached().length) {
      return of(cached());
    }

    return this.httpService
      .get<IbgeCity[]>(`${environment.api.ibge}/${uf}/municipios`)
      .pipe(
        map<IbgeCity[], Option<string | number>[]>((cities) =>
          cities
            .map((city) => ({
              display: city.nome,
              value: useIdAsValue ? city.id : city.nome
            }))
            .sort((a, b) => a.display.localeCompare(b.display))
        ),
        tap((cities) =>
          this.store.dispatch(
            IbgeActions.loadCities({
              cities,
              cacheKey
            })
          )
        )
      );
  }
}
